import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
/** Base URLs passed to the CE (aligned with Dividends bridge shape). */
export type WorkspaceBaseUrls = GtwElementsBaseUrlsModel & { base_url?: string };

/** Canonical header-data shape from AngularJS bridge (whole-migration-plan §8). */
export interface WorkspaceHeaderData {
  globalParams?: Record<string, unknown>;
  userSettingsData?: { user?: { sso_id?: string; user_name?: string; email?: string } };
  serverConfigData?: Record<string, unknown>;
  generalConfigData?: Record<string, unknown>;
  base_url?: string;
  workspaceFactory?: unknown;
  inputs?: unknown;
}

export interface WorkspaceTabItem {
  screen_key?: string;
  link?: string;
  label?: string;
  filter_inherit?: number;
  cachedData?: boolean;
  loading_data?: boolean;
  show_tab?: boolean;
  active?: boolean;
}

export interface WorkspaceScreenItem {
  screen_key?: string;
  label?: string;
  show_tab?: boolean;
  cachedData?: boolean;
  loading_data?: boolean;
}

export interface WorkspaceBridgeData {
  workspaces?: WorkspaceTabItem[];
  screens?: WorkspaceScreenItem[];
  isEnable?: boolean;
  backMessage?: string;
  loading?: boolean;
  title?: string;
  filterText?: string;
  showFilterToggle?: boolean;
}

export type WorkspaceActionType =
  | 'set-tab'
  | 'clear-cache'
  | 'back'
  | 'toggle-filters'
  | 'toggle-nav'
  | 'open-screen-drop'
  | 'show-screen'
  | 'hide-screen'
  | 'show-all'
  | 'hide-all'
  | 'clear-search'
  | 'set-filter'
  | 'cancel';

export interface WorkspaceActionPayload {
  type: WorkspaceActionType;
  screen_key?: string;
  link?: string;
  filterText?: string;
  [key: string]: unknown;
}

@Component({
    selector: 'gtw-workspace',
    templateUrl: './workspace.component.html',
    styleUrls: ['./workspace.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class WorkspaceComponent implements AfterViewInit, AfterViewChecked, OnDestroy {
  @Output('workspace-action') workspaceAction = new EventEmitter<WorkspaceActionPayload>();
  @ViewChild('tabContainer') tabContainerRef!: ElementRef<HTMLElement>;
  @ViewChild('dropdownToggleBtn') dropdownToggleBtnRef!: ElementRef<HTMLElement>;

  baseUrls: WorkspaceBaseUrls = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  headerData: WorkspaceHeaderData = {};
  rowData: Record<string, unknown> = {};
  filterParams: Record<string, unknown> = {};
  workspaceData: WorkspaceBridgeData = {};
  mode = 'workspace';

  searchText = '';
  minimise = false;

  hideButtons = true;
  disableLeft = true;
  disableRight = true;
  hideDropDown = true;
  showDropdownMenu = false;
  dropdownTabs: WorkspaceTabItem[] = [];
  dropdownMenuStyle: Record<string, string> = {};

  private scrollByPixels = 50;
  private mouseDownInterval: ReturnType<typeof setInterval> | null = null;
  private isHolding = false;
  private resizeTimeout: ReturnType<typeof setTimeout> | null = null;
  private needsRecalc = false;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  @Input('base-urls')
  set _base_urls(baseUrls: string) {
    try {
      const parsed =
        typeof baseUrls === 'string' ? (safeJsonInput<any>(baseUrls, null, 'workspace.parse1') as WorkspaceBaseUrls) : (baseUrls as WorkspaceBaseUrls);
      if (parsed && Object.keys(parsed).length > 0) {
        this.baseUrls = parsed;
        this.cdr.markForCheck();
      }
    } catch (e) {
      console.log('Base URLs input not yet stable.');
    }
  }

  @Input('header-data')
  set _header_data(headerData: string) {
    try {
      const parsed =
        typeof headerData === 'string'
          ? (safeJsonInput<any>(headerData, null, 'workspace.parse2') as WorkspaceHeaderData)
          : (headerData as WorkspaceHeaderData);
      if (parsed && Object.keys(parsed).length > 0) {
        this.headerData = parsed;
        this.cdr.markForCheck();
      }
    } catch (e) {
      console.log('Header data input not yet stable.');
    }
  }

  @Input('row-data')
  set _row_data(rowData: string) {
    try {
      this.rowData =
        typeof rowData === 'string' ? (safeJsonInput<any>(rowData, null, 'workspace.parse3') as Record<string, unknown>) : rowData || {};
      this.cdr.markForCheck();
    } catch (e) {
      console.log('Row data input not yet stable.');
    }
  }

  @Input('filter-params')
  set _filter_params(filterParams: string) {
    try {
      const parsed =
        typeof filterParams === 'string'
          ? (safeJsonInput<any>(filterParams, null, 'workspace.parse4') as Record<string, unknown>)
          : (filterParams as Record<string, unknown>);
      if (parsed && Object.keys(parsed).length > 0) {
        this.filterParams = parsed;
        this.cdr.markForCheck();
      }
    } catch (e) {
      console.log('Filter params input not yet stable.');
    }
  }

  @Input('workspace-data')
  set _workspace_data(workspaceData: string) {
    try {
      const parsed =
        typeof workspaceData === 'string'
          ? (safeJsonInput<any>(workspaceData, null, 'workspace.parse5') as WorkspaceBridgeData)
          : (workspaceData as WorkspaceBridgeData);
      this.workspaceData = parsed || {};
      this.searchText = String(this.workspaceData?.filterText ?? '');
      this.scheduleRecalc();
    } catch (e) {
      console.log('Workspace data input not yet stable.');
    }
  }

  @Input('mode')
  set _mode(mode: string) {
    this.mode = mode || 'workspace';
    this.cdr.markForCheck();
  }

  /* ── Lifecycle ─────────────────────────────────────────────── */

  ngAfterViewInit(): void {
    this.scheduleRecalc();
  }

  ngAfterViewChecked(): void {
    if (this.needsRecalc) {
      this.needsRecalc = false;
      setTimeout(() => {
        this.reCalcAll();
        this.scrollTabIntoView();
      });
    }
  }

  ngOnDestroy(): void {
    this.cancelMouseDownInterval();
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      this.reCalcAll();
      this.scrollTabIntoView();
      this.cdr.markForCheck();
    }, 250);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.showDropdownMenu) {
      this.showDropdownMenu = false;
      this.cdr.markForCheck();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.showDropdownMenu) {
      this.showDropdownMenu = false;
      this.cdr.markForCheck();
    }
  }

  /* ── Getters ───────────────────────────────────────────────── */

  get workspaces(): WorkspaceTabItem[] {
    return Array.isArray(this.workspaceData?.workspaces) ? this.workspaceData.workspaces : [];
  }

  get screens(): WorkspaceScreenItem[] {
    return Array.isArray(this.workspaceData?.screens) ? this.workspaceData.screens : [];
  }

  get filteredScreens(): WorkspaceScreenItem[] {
    const term = this.searchText.trim().toLowerCase();
    if (!term) {
      return this.screens;
    }
    return this.screens.filter((s) => String(s?.label ?? '').toLowerCase().includes(term));
  }

  /* ── Scroll logic (mirrors AngularJS scrollable-tabset) ──── */

  reCalcAll(): void {
    const container = this.tabContainerRef?.nativeElement;
    if (!container) { return; }
    this.hideButtons = container.scrollWidth <= container.offsetWidth;
    this.hideDropDown = this.hideButtons;
    if (!this.hideButtons) {
      this.reCalcSides();
    }
    this.cdr.markForCheck();
  }

  reCalcSides(): void {
    const container = this.tabContainerRef?.nativeElement;
    if (!container || this.hideButtons) { return; }
    this.disableRight = container.scrollLeft >= container.scrollWidth - container.offsetWidth - 1;
    this.disableLeft = container.scrollLeft <= 0;
    this.reCalcDropdownTabs();
  }

  scrollButtonDown(direction: string, event: MouseEvent): void {
    event.stopPropagation();
    this.isHolding = true;
    const change = direction === 'left' ? -this.scrollByPixels : this.scrollByPixels;
    this.animateScroll(change, 150, true);
    this.mouseDownInterval = setInterval(() => {
      if (this.isHolding) {
        this.animateScroll(change, 150, true);
      }
    }, 100);
  }

  scrollButtonUp(): void {
    this.isHolding = false;
    this.cancelMouseDownInterval();
  }

  onTabClick(workspace: WorkspaceTabItem): void {
    this.emitAction('set-tab', { screen_key: workspace.screen_key, link: workspace.link });
    setTimeout(() => this.scrollTabIntoView(), 300);
  }

  onDropdownTabClick(tab: WorkspaceTabItem): void {
    this.showDropdownMenu = false;
    this.emitAction('set-tab', { screen_key: tab.screen_key, link: tab.link });
    setTimeout(() => this.scrollTabIntoView(), 300);
  }

  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.showDropdownMenu) {
      this.reCalcDropdownTabs();
      const btn = this.dropdownToggleBtnRef?.nativeElement;
      if (btn) {
        const rect = btn.getBoundingClientRect();
        this.dropdownMenuStyle = {
          position: 'fixed',
          top: rect.bottom + 2 + 'px',
          right: (window.innerWidth - rect.right) + 'px',
          left: 'auto',
        };
      }
    }
    this.showDropdownMenu = !this.showDropdownMenu;
    this.cdr.markForCheck();
  }

  scrollTabIntoView(): void {
    const container = this.tabContainerRef?.nativeElement;
    if (!container || this.hideButtons) { return; }
    const activeTab = container.querySelector('li.active') as HTMLElement;
    if (!activeTab) { return; }
    const containerRect = container.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    const leftPos = tabRect.left - containerRect.left;
    const rightPos = leftPos + tabRect.width;
    if (leftPos < 0) {
      this.animateScroll(leftPos, 700, false);
    } else if (rightPos > container.offsetWidth) {
      this.animateScroll(rightPos - container.offsetWidth, 700, false);
    }
  }

  /* ── Actions ───────────────────────────────────────────────── */

  emitAction(type: WorkspaceActionType, payload: Record<string, unknown> = {}): void {
    this.workspaceAction.emit({ type, ...payload } as WorkspaceActionPayload);
  }

  cacheStatusTitle(hasCache: boolean | undefined): string {
    return hasCache ? 'Clear Data Cache' : '';
  }

  screenDropCacheTitle(screen: WorkspaceScreenItem): string {
    return screen.cachedData ? 'Clear cached data' : '';
  }

  /* ── Private helpers ───────────────────────────────────────── */

  private scheduleRecalc(): void {
    this.needsRecalc = true;
    this.cdr.markForCheck();
  }

  private cancelMouseDownInterval(): void {
    this.isHolding = false;
    if (this.mouseDownInterval) {
      clearInterval(this.mouseDownInterval);
      this.mouseDownInterval = null;
    }
  }

  private animateScroll(change: number, duration: number, linear: boolean): void {
    const container = this.tabContainerRef?.nativeElement;
    if (!container) { return; }
    const start = container.scrollLeft;
    const increment = 20;
    let elapsed = 0;
    const step = () => {
      elapsed += increment;
      container.scrollLeft = linear
        ? this.linearTween(elapsed, start, change, duration)
        : this.easeInOutQuad(elapsed, start, change, duration);
      if (elapsed < duration) {
        setTimeout(step, increment);
      } else {
        this.reCalcSides();
        this.cdr.markForCheck();
      }
    };
    step();
  }

  private linearTween(t: number, start: number, change: number, duration: number): number {
    return (change * t) / duration + start;
  }

  private easeInOutQuad(t: number, start: number, change: number, duration: number): number {
    let ct = t / (duration / 2);
    if (ct < 1) { return (change / 2) * ct * ct + start; }
    ct--;
    return (-change / 2) * (ct * (ct - 2) - 1) + start;
  }

  private reCalcDropdownTabs(): void {
    const container = this.tabContainerRef?.nativeElement;
    if (!container || this.hideButtons) {
      this.dropdownTabs = [];
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const tabElements = container.querySelectorAll('li');
    const allWs = this.workspaces;
    const overflow: WorkspaceTabItem[] = [];
    tabElements.forEach((tabEl, i) => {
      const tabRect = tabEl.getBoundingClientRect();
      const leftPos = tabRect.left - containerRect.left;
      const rightPos = leftPos + tabRect.width;
      if (leftPos < 0 || rightPos > container.offsetWidth) {
        if (i < allWs.length) {
          overflow.push(allWs[i]);
        }
      }
    });
    this.dropdownTabs = overflow;
  }
}
